'use client';

import Script from 'next/script';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

declare global {
    interface Window {
        turnstile?: {
            render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
            remove: (widgetId: string) => void;
        };
    }
}

type TurnstileRenderOptions = {
    sitekey: string;
    callback?: (token: string) => void;
    'error-callback'?: () => void;
    'expired-callback'?: () => void;
};

type TurnstileWidgetProps = {
    /** Called with the verification token once solved, and with `null` on expiry, error, or reset. */
    onVerify: (token: string | null) => void;
    className?: string;
};

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

/**
 * Cloudflare Turnstile managed widget (CIN-13378 spam protection for cinema-website forms).
 * Renders nothing when NEXT_PUBLIC_TURNSTILE_SITE_KEY isn't set, so local/dev and any
 * deployment without a configured Turnstile site keep working without captcha handling.
 */
export default function TurnstileWidget({ onVerify, className }: TurnstileWidgetProps) {
    const t = useTranslations();
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [hasError, setHasError] = useState(false);

    const renderWidget = () => {
        if (!TURNSTILE_SITE_KEY || !window.turnstile || !containerRef.current) {
            return;
        }

        // onReady also fires on client-side route re-mounts; drop any previous instance first.
        if (widgetIdRef.current) {
            window.turnstile.remove(widgetIdRef.current);
            widgetIdRef.current = null;
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            callback: (token) => {
                setHasError(false);
                onVerify(token);
            },
            'error-callback': () => {
                setHasError(true);
                onVerify(null);
            },
            'expired-callback': () => {
                onVerify(null);
            }
        });
    };

    useEffect(
        () => () => {
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        },
        []
    );

    if (!TURNSTILE_SITE_KEY) {
        return null;
    }

    return (
        <div className={cn('mb-8', className)}>
            <Script
                id="cf-turnstile-script"
                src={TURNSTILE_SCRIPT_SRC}
                strategy="afterInteractive"
                onReady={renderWidget}
            />
            <div ref={containerRef} />
            {hasError && <p className="text-body-12 text-red mt-2">{t('common.captcha-error')}</p>}
        </div>
    );
}
